import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Check, X, User } from "lucide-react";
import { adminServices } from "../services/AdminServices";
import { toast } from "react-toastify";

const BucorItemApprovalDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestData = location.state?.requestData || {
    requestId: "REQ-25-01001",
    requisitor: "IPX Admin",
    facility: "NBP - Minimum"
  };

  const [selectedItems, setSelectedItems] = useState([]);
  const [itemTypes, setItemTypes] = useState({});
  const [reasonForListing, setReasonForListing] = useState(requestData?.reasonForListing || "");
  const [bucorRemarks, setBucorRemarks] = useState(requestData?.remarks || requestData?.notesRemarks || "");
  const [loading, setLoading] = useState(false);

  // Use real item data from API response, fall back to empty array
  const itemsData = (requestData?.bucorApprovalItems ?? requestData?.items ?? []).length > 0
    ? (requestData.bucorApprovalItems ?? requestData.items)
    : [
        { id: 1, description: "Item Description here", uom: "Box", qty: "10", itemType: "", moq: "-" },
        { id: 2, description: "Item Description here", uom: "Box", qty: "10", itemType: "", moq: "-" },
        { id: 3, description: "Item Description here", uom: "Box", qty: "10", itemType: "", moq: "-" }
      ];

  const handleItemTypeChange = (itemId, value) => {
    setItemTypes(prev => ({ ...prev, [itemId]: value }));
  };

  const handleCheckboxChange = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Update status using API endpoint
  const updateStatus = async (status, remarks = "") => {
    const approvalId = requestData?.id || requestData?.originalData?.id;
    
    if (!approvalId) {
      toast.error("Error: Approval ID not found");
      return false;
    }

    setLoading(true);
    try {
      const response = await adminServices.updateData(
        `BucorApprovals/UpdateStatus/${approvalId}`,
        {
          status: status,
          remarks: remarks || bucorRemarks || ""
        }
      );

      if (response.status === 200 || response.status === 204) {
        return true;
      } else {
        toast.error("Failed to update status");
        return false;
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.message || "Failed to update status. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSelected = async () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item to approve.");
      return;
    }
    
    const success = await updateStatus("Approved", bucorRemarks);
    if (success) {
      toast.success("Items approved successfully!");
      navigate("/bucor-module");
    }
  };

  const handleRejectSelected = async () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item to reject.");
      return;
    }
    
    if (!bucorRemarks.trim()) {
      toast.warning("Please provide remarks for rejection.");
      return;
    }
    
    const success = await updateStatus("Rejected", bucorRemarks);
    if (success) {
      toast.success("Items rejected.");
      navigate("/bucor-module");
    }
  };

  const handleCompleteRequest = async () => {
    if (!bucorRemarks.trim()) {
      toast.warning("Please provide Bucor remarks before completing the request.");
      return;
    }

    const success = await updateStatus("Approved", bucorRemarks);
    if (success) {
      toast.success("Request completed successfully!");
      navigate("/bucor-module");
    }
  };

  return (
    <div className="dashboard-content">
      <div className="bucor-item-approval-detail-page">
        {/* Header */}
        <div className="bucor-item-approval-detail-header">
          <button 
            className="bucor-item-approval-detail-back-btn"
            onClick={() => navigate("/bucor-module")}
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <h1 className="bucor-item-approval-detail-request-id">{requestData.requestId}</h1>
        </div>

        {/* Item Details Section */}
        <div className="bucor-item-approval-detail-section">
          <div className="bucor-item-approval-detail-section-header">
            <h2 className="bucor-item-approval-detail-section-title">Item Details</h2>
            <div className="bucor-item-approval-detail-actions-top">
              <span className="bucor-item-approval-selected-count">
                {selectedItems.length} selected
              </span>
              <button 
                className="bucor-item-approval-approve-btn"
                onClick={handleApproveSelected}
              >
                <Check size={18} />
                Approve Selected
              </button>
              <button 
                className="bucor-item-approval-reject-btn"
                onClick={handleRejectSelected}
              >
                <X size={18} />
                Reject Selected
              </button>
            </div>
          </div>

          <div className="bucor-item-approval-detail-table-container">
            <table className="bucor-item-approval-detail-table">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th>Item Description</th>
                  <th>UOM</th>
                  <th>QTY</th>
                  <th>Item Type</th>
                  <th>MOQ</th>
                </tr>
              </thead>
              <tbody>
                {itemsData.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleCheckboxChange(item.id)}
                      />
                    </td>
                    <td>{item.description ?? item.itemDescription ?? item.name ?? "-"}</td>
                    <td>{item.uom ?? item.unitOfMeasure ?? "-"}</td>
                    <td>{item.qty ?? item.quantity ?? "-"}</td>
                    <td>
                      <select
                        className="bucor-item-approval-item-type-select"
                        value={itemTypes[item.id] ?? item.itemType ?? ""}
                        onChange={(e) => handleItemTypeChange(item.id, e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="REGULAR">Regular</option>
                        <option value="REGULATED">Regulated</option>
                        <option value="SPECIAL_ORDER">Special Order</option>
                      </select>
                    </td>
                    <td>{item.moq ?? item.minimumOrderQuantity ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reason for listing */}
        <div className="bucor-item-approval-detail-section">
          <label className="bucor-item-approval-detail-label">Reason for listing</label>
          <textarea
            className="bucor-item-approval-reason-textarea"
            value={reasonForListing}
            onChange={(e) => setReasonForListing(e.target.value)}
            placeholder="Enter reason for listing..."
            rows={4}
          />
        </div>

        {/* Bucor Remarks */}
        <div className="bucor-item-approval-detail-section">
          <label className="bucor-item-approval-detail-label">Bucor Remarks</label>
          <textarea
            className="bucor-item-approval-remarks-textarea"
            value={bucorRemarks}
            onChange={(e) => setBucorRemarks(e.target.value)}
            placeholder="Enter remarks..."
            rows={4}
          />
        </div>

        {/* Complete Request Button */}
        <div className="bucor-item-approval-detail-actions">
          <button 
            className="bucor-item-approval-complete-btn"
            onClick={handleCompleteRequest}
          >
            Complete Request
          </button>
        </div>

        {/* Activity Log Section */}
        <div className="bucor-item-approval-activity-log-section">
          <h2 className="bucor-item-approval-activity-log-title">Activity Log</h2>
          <div className="bucor-item-approval-activity-log-list">
            {[1, 2, 3].map((item, index) => (
              <div key={index} className="bucor-item-approval-activity-log-item">
                <div className="bucor-item-approval-activity-log-icon">
                  <User size={20} />
                </div>
                <div className="bucor-item-approval-activity-log-content">
                  <div className="bucor-item-approval-activity-log-user">Approver's name</div>
                  <div className="bucor-item-approval-activity-log-action">Viewed request</div>
                </div>
                <div className="bucor-item-approval-activity-log-timestamp">
                  MM/DD/YYYY, HH:MM:SS
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BucorItemApprovalDetail;

